import {
  Box,
  Button,
  HStack,
  Icon,
  Image,
  SimpleGrid,
  Skeleton,
  Text,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { FiExternalLink, FiTrendingDown, FiTrendingUp } from "react-icons/fi";
import { IconType } from "react-icons";
import { useNavigate } from "react-router-dom";
import { TadbirFund } from "../../hooks/useFunds";
import {
  getPublicStaticFundRoute,
  isPublicStaticFund,
} from "../../data/publicStaticFunds";
import {
  formatFundCurrency,
  formatFundNumber,
  formatFundPercent,
  isFundInvestmentEnabled,
  getFundRedemptionPriceLabel,
  getFundLogoSrc,
  getFundSubscriptionPriceLabel,
} from "./fundCardUtils";

const MotionBox = motion(Box);

interface FundCardAction {
  label: string;
  onClick: () => void;
  icon: IconType;
  isLoading?: boolean;
  isDisabled?: boolean;
}

interface FundCardProps {
  fund: TadbirFund;
  index?: number;
  primaryAction: FundCardAction;
  variant?: "default" | "short";
  showSecondaryAction?: boolean;
}

const StatItem = ({ label, value }: { label: string; value: string }) => {
  const statBg = useColorModeValue("gray.50", "dark.900");
  const labelColor = useColorModeValue("gray.500", "gray.400");
  const valueColor = useColorModeValue("gray.800", "white");

  return (
    <Box bg={statBg} borderRadius="xl" px={4} py={3} textAlign="center">
      <Text fontSize="xs" color={labelColor} mb={1}>
        {label}
      </Text>
      <Text fontSize="sm" fontWeight="bold" color={valueColor} noOfLines={1}>
        {value}
      </Text>
    </Box>
  );
};

const FundCardTopSection = ({
  fund,
  logoSrc,
  logoSize,
}: {
  fund: TadbirFund;
  logoSrc: string | null;
  logoSize: string;
}) => {
  const titleColor = useColorModeValue("gray.800", "white");
  const captionColor = useColorModeValue("gray.500", "gray.400");
  const fallbackBg = useColorModeValue("whiteAlpha.700", "whiteAlpha.100");

  return (
    <Box w="100%" borderRadius="2xl">
      <HStack spacing={3} align="center">
        <Box
          w={logoSize}
          h={logoSize}
          borderRadius="xl"
          display="flex"
          alignItems="center"
          justifyContent="center"
          overflow="hidden"
          flexShrink={0}
        >
          {logoSrc ? (
            <Image
              src={logoSrc}
              alt={fund.fundSummaryName?.trim() || fund.fundName.trim()}
              maxW="100%"
              maxH="100%"
              objectFit="contain"
            />
          ) : (
            <Box
              w="100%"
              h="100%"
              borderRadius="xl"
              bg={fallbackBg}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Text color={captionColor} fontWeight="bold" fontSize="xs">
                {fund.fundRegisterCode}
              </Text>
            </Box>
          )}
        </Box>

        <VStack align="start" spacing={1} flex="1" minW={0}>
          <Text
            fontWeight="bold"
            whiteSpace="normal"
            textAlign="right"
            color={titleColor}
            noOfLines={3}
          >
            {fund.fundSummaryName?.trim() || fund.fundName.trim()}
          </Text>
          <Text fontSize="xs" color={captionColor}>
            {fund.fundName.trim() || fund.fundTypeTitle.trim()}
          </Text>
        </VStack>
      </HStack>
    </Box>
  );
};

export const FundCard = ({
  fund,
  index = 0,
  primaryAction,
  variant = "default",
  showSecondaryAction = true,
}: FundCardProps) => {
  const navigate = useNavigate();
  const cardBg = useColorModeValue("white", "dark.50");
  const subtitleColor = useColorModeValue("gray.500", "gray.400");
  const borderColor = useColorModeValue("gray.100", "gray.700");
  const heroBg = useColorModeValue("gray.50", "dark.900");
  const newBg = useColorModeValue("gray.50", "dark.900");

  const logoSrc = getFundLogoSrc(fund.fundRegisterCode);
  const isPositiveAnnual = fund.annualReturn >= 0;
  const AnnualTrendIcon = isPositiveAnnual ? FiTrendingUp : FiTrendingDown;
  const isShort = variant === "short";
  const isStaticFund = isPublicStaticFund(fund);
  const isInvestmentDisabled = !isFundInvestmentEnabled(
    fund.fundRegisterCode,
  );
  const staticContent = isStaticFund ? fund.staticContent : undefined;
  const shouldShowSecondaryAction = showSecondaryAction;
  const subscriptionPriceLabel = getFundSubscriptionPriceLabel(
    fund.fundRegisterCode,
  );
  const redemptionPriceLabel = getFundRedemptionPriceLabel(
    fund.fundRegisterCode,
  );

  return (
    <MotionBox
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.45, delay: index * 0.08, ease: "easeOut" }}
      h="100%"
    >
      <Box
        bg={isShort ? newBg : cardBg}
        borderRadius="2xl"
        border="1px solid"
        borderColor={borderColor}
        shadow="sm"
        _hover={{
          shadow: "xl",
          transform: "translateY(-4px)",
          borderColor: "brand.200",
        }}
        transition="all 0.3s ease"
        h="100%"
        display="flex"
        flexDirection="column"
        overflow="hidden"
      >
        <Box
          p={6}
          flex="1"
          display="flex"
          flexDirection="column"
          justifyContent={isShort ? "center" : "flex-start"}
        >
          {isShort ? (
            <VStack spacing={5} textAlign="center" flex="1" justify="center">
              <FundCardTopSection
                fund={fund}
                logoSrc={logoSrc}
                logoSize="85px"
              />
            </VStack>
          ) : (
            <>
              <Box mb={5}>
                <FundCardTopSection
                  fund={fund}
                  logoSrc={logoSrc}
                  logoSize="85px"
                />
              </Box>

              <SimpleGrid columns={2} spacing={3} flex="1" alignContent="start">
                <StatItem
                  label={
                    isStaticFund ? "قیمت آخرین معامله" : subscriptionPriceLabel
                  }
                  value={
                    isStaticFund && staticContent
                      ? staticContent.lastTradePrice
                      : `${formatFundNumber(fund.nav.subscriptionNav)} ریال`
                  }
                />
                <StatItem
                  label={isStaticFund ? "قیمت پایانی" : redemptionPriceLabel}
                  value={
                    isStaticFund && staticContent
                      ? staticContent.closingPrice
                      : `${formatFundNumber(fund.nav.cancelNav)} ریال`
                  }
                />
                <StatItem
                  label="ارزش خالص دارایی"
                  value={formatFundCurrency(fund.nav.totalNetAssetValue)}
                />
                <StatItem
                  label="تعداد سرمایه‌گذاران"
                  value={`${formatFundNumber(fund.nav.totalInvestor)} نفر`}
                />
              </SimpleGrid>

              <Box
                bg={heroBg}
                borderRadius="2xl"
                p={3}
                mt={5}
                textAlign="center"
              >
                <Text fontSize="sm" color={subtitleColor} mb={1}>
                  بازدهی سالانه
                </Text>
                <HStack spacing={2} justify="center">
                  <Icon
                    as={AnnualTrendIcon as any}
                    color={isPositiveAnnual ? "green.500" : "red.500"}
                    boxSize={5}
                  />
                  <Text
                    fontSize="2xl"
                    fontWeight="extrabold"
                    color={isPositiveAnnual ? "green.500" : "red.500"}
                  >
                    {isStaticFund && staticContent
                      ? staticContent.averageAnnualReturn
                      : `${formatFundPercent(fund.annualReturn)}%`}
                  </Text>
                </HStack>
              </Box>
            </>
          )}
        </Box>

        <VStack spacing={3} p={6} pt={0}>
          <Button
            w="100%"
            colorScheme="brand"
            rightIcon={<Icon as={primaryAction.icon as any} />}
            onClick={primaryAction.onClick}
            isLoading={primaryAction.isLoading}
            isDisabled={primaryAction.isDisabled || isInvestmentDisabled}
            fontWeight="bold"
          >
            {primaryAction.label}
          </Button>
          {shouldShowSecondaryAction && (
            <Button
              w="100%"
              variant="outline"
              colorScheme="brand"
              rightIcon={<Icon as={FiExternalLink as any} />}
              onClick={() => navigate(`/funds/${getPublicStaticFundRoute(fund)}`)}
            >
              اطلاعات بیشتر
            </Button>
          )}
        </VStack>
      </Box>
    </MotionBox>
  );
};

export const FundCardSkeleton = () => {
  const cardBg = useColorModeValue("white", "dark.50");
  const borderColor = useColorModeValue("gray.100", "gray.700");

  return (
    <Box
      bg={cardBg}
      borderRadius="2xl"
      border="1px solid"
      borderColor={borderColor}
      shadow="sm"
      p={6}
      h="100%"
    >
      <HStack justify="space-between" align="start" mb={5}>
        <VStack align="start" spacing={3} flex="1">
          <Skeleton height="24px" width="96px" borderRadius="full" />
          <Skeleton height="22px" width="80%" borderRadius="md" />
        </VStack>
        <Skeleton height="68px" width="68px" borderRadius="2xl" />
      </HStack>
      <Skeleton height="96px" borderRadius="2xl" mb={5} />
      <SimpleGrid columns={2} spacing={3} mb={5}>
        {Array.from({ length: 4 }).map((_, index) => (
          <Skeleton key={index} height="72px" borderRadius="xl" />
        ))}
      </SimpleGrid>
      <Skeleton height="40px" borderRadius="lg" mb={3} />
      <Skeleton height="40px" borderRadius="lg" />
    </Box>
  );
};
